import Cuboid from '../rendering/Cuboid.js';
import Material from '../materials/Material.js';
import DOMUtils from '../utils/DOMUtils.js';

// Default texture dimensions for triplanar preview (common texture size)
// This should ideally match the actual texture dimensions loaded
const DEFAULT_TEXTURE_SIZE = 256;

/**
 * Visual preview for cuboid placement/resize operations
 * Shows a semi-transparent ghost cuboid at the target position
 * Applies triplanar UV projection for accurate texture preview
 */
export default class GhostPreview {
  /**
   * Create a new ghost preview
   * @param {World} world - The world instance
   */
  constructor(world) {
    this.world = world;
    this.cuboid = null;
    this.isValid = true;
    this.currentPosition = null;
    this.presetMaterial = null; // Single material for all faces (Add tool presets)
    this.faceMaterials = null; // Per-face materials (Move/Extend tools)
    this.useTriplanar = false; // Whether to apply triplanar UV projection

    // UV caching to avoid redundant DOM updates
    this.lastUVState = null; // Cache key for last applied UV state
    this.lastZoomLevel = null; // Track zoom to invalidate UV cache when cuboid materials refresh

    // We'll add the ghost directly to #world element
    // It will be re-appended after each world update
  }

  /**
   * Set the preset material for the ghost preview (same material for all faces)
   * Used by Add tool for placing new cuboids
   * @param {Material|null} material - The material or null to use default
   * @param {boolean} useTriplanar - Whether to apply triplanar UV projection
   */
  setPresetMaterial(material, useTriplanar = false) {
    this.presetMaterial = material;
    this.faceMaterials = null; // Clear per-face materials
    this.useTriplanar = useTriplanar;
    this.lastUVState = null; // Invalidate UV cache on material change
    // Force full recreation of ghost to show new material
    if (this.cuboid) {
      const pos = this.getPosition();
      const wasValid = this.isValid;
      this.hide(); // Destroy existing ghost completely
      if (pos) {
        this.show(pos.x, pos.y, pos.z, pos.width, pos.height, pos.depth, wasValid);
      }
    }
  }

  /**
   * Set per-face materials for the ghost preview
   * Used by Move/Extend tools to show actual cuboid materials
   * @param {Object|null} materials - Object with materials for each face, or null to clear
   */
  setFaceMaterials(materials) {
    this.faceMaterials = materials;
    this.presetMaterial = null; // Clear single preset material
    this.lastUVState = null; // Invalidate UV cache on material change
    // Force full recreation of ghost to show new materials
    if (this.cuboid) {
      const pos = this.getPosition();
      const wasValid = this.isValid;
      this.hide(); // Destroy existing ghost completely
      if (pos) {
        this.show(pos.x, pos.y, pos.z, pos.width, pos.height, pos.depth, wasValid);
      }
    }
  }

  /**
   * Create ghost material for a face
   * @param {boolean} isValid - Whether placement is valid
   * @param {string} face - The face name (top, bottom, front, back, left, right)
   * @return {Material} Semi-transparent material
   */
  createGhostMaterial(isValid, face) {
    // If we have per-face materials (Move/Extend tools), use the specific face material
    if (this.faceMaterials && this.faceMaterials[face] && isValid) {
      const material = this.cloneMaterial(this.faceMaterials[face]);
      material.opacity = 0.7;
      material.borderWidth = 2;
      material.borderColor = '#4CAF50';
      material.borderStyle = 'solid';
      return material;
    }

    // If we have a preset material (Add tool), clone it and add transparency
    if (this.presetMaterial && isValid) {
      const material = this.cloneMaterial(this.presetMaterial);
      material.opacity = 0.7;
      material.borderWidth = 2;
      material.borderColor = '#4CAF50';
      material.borderStyle = 'solid';
      return material;
    }

    // Default ghost material (colored overlay)
    const color = isValid ? 'rgba(76, 175, 80, 0.5)' : 'rgba(244, 67, 54, 0.5)';
    const material = Material.color(color);
    material.borderWidth = 3;
    material.borderColor = isValid ? '#4CAF50' : '#F44336';
    material.borderStyle = 'solid';
    return material;
  }

  /**
   * Clone a material
   * @param {Material} material - Material to clone
   * @return {Material} Cloned material
   */
  cloneMaterial(material) {
    return new Material({
      type: material.type,
      color: material.color,
      opacity: material.opacity,
      texture: material.texture,
      video: material.video,
      text: material.text,
      textColor: material.textColor,
      textSize: material.textSize,
      textFont: material.textFont,
      gradient: material.gradient ? { ...material.gradient } : null,
      pattern: material.pattern ? { ...material.pattern } : null,
      borderColor: material.borderColor,
      borderWidth: material.borderWidth,
      className: material.className,
      scaleX: material.scaleX,
      scaleY: material.scaleY,
      scaleZ: material.scaleZ,
      offsetX: material.offsetX,
      offsetY: material.offsetY,
      offsetZ: material.offsetZ,
      noCollision: material.noCollision
    });
  }

  /**
   * Show or update the ghost preview
   * @param {number} x - X position
   * @param {number} y - Y position
   * @param {number} z - Z position
   * @param {number} width - Width
   * @param {number} height - Height
   * @param {number} depth - Depth
   * @param {boolean} isValid - Whether placement is valid (no collision)
   */
  show(x, y, z, width, height, depth, isValid) {
    const posKey = `${x},${y},${z},${width},${height},${depth},${isValid}`;

    // If nothing has changed, just ensure transforms are up to date
    if (this.currentPosition === posKey && this.cuboid) {
      this.cuboid.update(this.world.camera);
      // UV doesn't need recalculating - position hasn't changed
      this.ensureInDOM();
      return;
    }

    // Reuse existing cuboid if one exists - update position, dimensions, and validity in place
    // This avoids DOM recreation which causes mobile flashing
    if (this.cuboid) {
      // Update position and dimensions
      this.cuboid.x = x;
      this.cuboid.y = y;
      this.cuboid.z = z;
      this.cuboid.width = width;
      this.cuboid.height = height;
      this.cuboid.depth = depth;
      this.cuboid.needsUpdate = true;

      // Update validity/materials if changed
      if (this.isValid !== isValid) {
        this.isValid = isValid;
        this.lastUVState = null; // Invalidate UV cache since materials are being re-applied
        const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
        const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
        faces.forEach(face => {
          const material = this.createGhostMaterial(isValid, face);
          this.cuboid.materials[face] = material;
          if (this.cuboid.faceElements[face]) {
            material.apply(this.cuboid.faceElements[face], effectiveZoom);
            // Ensure pointer-events stay disabled after material update
            this.cuboid.faceElements[face].style.pointerEvents = 'none';
          }
        });
      }

      this.currentPosition = posKey;
      this.cuboid.update(this.world.camera);
      this.applyTriplanarUV();
      this.ensureInDOM();
      return;
    }

    // No cuboid exists - create one
    this.currentPosition = posKey;
    this.isValid = isValid;

    // Create a new cuboid for the ghost
    this.cuboid = new Cuboid({
      x, y, z,
      width, height, depth,
      cubeSize: this.world.cubeSize,
      materials: {
        top: this.createGhostMaterial(isValid, 'top'),
        bottom: this.createGhostMaterial(isValid, 'bottom'),
        front: this.createGhostMaterial(isValid, 'front'),
        back: this.createGhostMaterial(isValid, 'back'),
        left: this.createGhostMaterial(isValid, 'left'),
        right: this.createGhostMaterial(isValid, 'right')
      }
    });

    // Add ghost class and disable pointer events on container AND all faces
    this.cuboid.element.classList.add('ghost-preview');
    this.cuboid.element.style.pointerEvents = 'none';
    // Also disable on all face elements to ensure events pass through
    Object.values(this.cuboid.faceElements).forEach(faceEl => {
      if (faceEl) faceEl.style.pointerEvents = 'none';
    });

    // Track initial zoom level for cache invalidation
    this.lastZoomLevel = this.world.camera.zoomLevel;

    // Update the ghost to match camera
    this.cuboid.update(this.world.camera);

    // Apply triplanar UV for accurate texture preview
    this.applyTriplanarUV();

    // Add directly to the world element (same as other cuboids)
    this.ensureInDOM();
  }

  /**
   * Ensure the ghost element is in the DOM
   * Called after world updates which clear the world element
   */
  ensureInDOM() {
    if (this.cuboid && this.cuboid.element.parentNode !== this.world.element) {
      this.world.element.appendChild(this.cuboid.element);
    }
  }

  /**
   * Update the ghost preview with new validity state
   * @param {boolean} isValid - Whether placement is valid
   */
  updateValidity(isValid) {
    if (!this.cuboid || this.isValid === isValid) return;

    this.isValid = isValid;
    this.lastUVState = null; // Invalidate UV cache since materials are being re-applied

    // Update face materials
    const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    faces.forEach(face => {
      const material = this.createGhostMaterial(isValid, face);
      this.cuboid.materials[face] = material;
      if (this.cuboid.faceElements[face]) {
        material.apply(this.cuboid.faceElements[face], effectiveZoom);
        // Ensure pointer-events stay disabled after material update
        this.cuboid.faceElements[face].style.pointerEvents = 'none';
      }
    });

    // Re-apply triplanar UV after material update
    this.applyTriplanarUV();
  }

  /**
   * Hide and remove the ghost preview
   */
  hide() {
    if (this.cuboid) {
      this.cuboid.element.remove();
      this.cuboid = null;
      this.currentPosition = null;
      this.lastUVState = null; // Clear UV cache
      this.lastZoomLevel = null; // Clear zoom tracking
    }
  }

  /**
   * Update the ghost's visual transform to match camera
   */
  update() {
    if (this.cuboid) {
      // Check if zoom changed - Cuboid.update() will re-apply materials which clears UV settings
      const currentZoom = this.world.camera.zoomLevel;
      if (this.lastZoomLevel !== null && this.lastZoomLevel !== currentZoom) {
        // Invalidate UV cache so applyTriplanarUV() will re-apply after material refresh
        this.lastUVState = null;
      }
      this.lastZoomLevel = currentZoom;

      this.cuboid.update(this.world.camera);
      // Apply triplanar UV (position-dependent)
      this.applyTriplanarUV();
      // Re-append to world element in case it was cleared by World.update()
      this.ensureInDOM();
    }
  }

  /**
   * Check if ghost is currently visible
   * @return {boolean} True if visible
   */
  isVisible() {
    return this.cuboid !== null;
  }

  /**
   * Get the current ghost position and dimensions
   * @return {Object|null} Position {x, y, z, width, height, depth} or null
   */
  getPosition() {
    if (!this.cuboid) return null;
    return {
      x: this.cuboid.x,
      y: this.cuboid.y,
      z: this.cuboid.z,
      width: this.cuboid.width,
      height: this.cuboid.height,
      depth: this.cuboid.depth
    };
  }

  /**
   * Apply texture sizing to ghost faces for accurate preview
   * - Triplanar mode: tiles texture based on world position
   * - Single-face mode: scales texture to fit each face
   * Uses caching to avoid redundant DOM updates
   */
  applyTriplanarUV() {
    if (!this.cuboid) return;

    // If no texture material, clear any stale background styles and cache
    if (!this.presetMaterial || this.presetMaterial.type !== 'texture') {
      if (this.lastUVState !== null) {
        this.lastUVState = null;
        // Clear background styles that might be stale from previous texture
        const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
        faces.forEach(face => {
          const faceEl = this.cuboid.faceElements[face];
          if (faceEl) {
            faceEl.style.backgroundPosition = '';
            faceEl.style.backgroundSize = '';
            faceEl.style.backgroundRepeat = '';
          }
        });
      }
      return;
    }

    // On Android, zoom is baked into dimensions; on other platforms, CSS scale handles it
    const effectiveZoom = DOMUtils.getEffectiveZoom(this.world.camera.zoomLevel);
    const cubeSize = this.world.cubeSize * effectiveZoom;

    // Build state key to detect if UV actually needs updating
    // Include texture URL to handle texture changes at same position
    const textureUrl = this.presetMaterial.texture || '';
    const uvStateKey = `${this.cuboid.x},${this.cuboid.y},${this.cuboid.z},` +
      `${this.cuboid.width},${this.cuboid.height},${this.cuboid.depth},` +
      `${this.useTriplanar},${textureUrl}`;

    // Skip DOM updates if state hasn't changed
    if (this.lastUVState === uvStateKey) return;
    this.lastUVState = uvStateKey;

    const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];

    if (this.useTriplanar) {
      // Triplanar mode: tile texture based on world position
      // On Android, zoom is baked in; on other platforms, CSS scale handles it
      const texWidth = DEFAULT_TEXTURE_SIZE * effectiveZoom;
      const texHeight = DEFAULT_TEXTURE_SIZE * effectiveZoom;
      const bgSize = `${texWidth}px ${texHeight}px`;

      for (let i = 0; i < faces.length; i++) {
        const face = faces[i];
        const faceEl = this.cuboid.faceElements[face];
        if (!faceEl) continue;

        const { u, v } = this.calculateWorldUV(face, cubeSize);
        const bgPosX = -((u % texWidth + texWidth) % texWidth);
        const bgPosY = -((v % texHeight + texHeight) % texHeight);

        faceEl.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
        faceEl.style.backgroundSize = bgSize;
        faceEl.style.backgroundRepeat = 'repeat';
      }
    } else {
      // Single-face mode: scale texture to fit each face
      const w = this.cuboid.width * cubeSize;
      const h = this.cuboid.height * cubeSize;
      const d = this.cuboid.depth * cubeSize;

      for (let i = 0; i < faces.length; i++) {
        const face = faces[i];
        const faceEl = this.cuboid.faceElements[face];
        if (!faceEl) continue;

        // Calculate face dimensions based on orientation
        let faceWidth, faceHeight;
        switch (face) {
          case 'top':
          case 'bottom':
            faceWidth = w;
            faceHeight = d;
            break;
          case 'front':
          case 'back':
            faceWidth = w;
            faceHeight = h;
            break;
          case 'left':
          case 'right':
            faceWidth = d;
            faceHeight = h;
            break;
        }

        faceEl.style.backgroundPosition = '0 0';
        faceEl.style.backgroundSize = `${faceWidth}px ${faceHeight}px`;
        faceEl.style.backgroundRepeat = 'no-repeat';
      }
    }
  }

  /**
   * Calculate world-space UV coordinates for triplanar projection
   * Matches MaterialGroupManager.calculateWorldUV() for consistency
   * @param {string} face - The face name
   * @param {number} cubeSize - The base cube size (zoom applied via CSS scale on #world)
   * @return {Object} { u, v } world-space coordinates
   */
  calculateWorldUV(face, cubeSize) {
    const worldX = this.cuboid.x * cubeSize;
    const worldY = this.cuboid.y * cubeSize;
    const worldZ = this.cuboid.z * cubeSize;
    const w = this.cuboid.width * cubeSize;
    const h = this.cuboid.height * cubeSize;
    const d = this.cuboid.depth * cubeSize;

    let u, v;

    // Same UV formulas as MaterialGroupManager.calculateWorldUV()
    switch (face) {
      case 'top':
        u = worldX;
        v = -(worldZ + d);
        break;
      case 'bottom':
        u = worldX;
        v = worldZ;
        break;
      case 'front':
        u = worldX;
        v = -(worldY + h);
        break;
      case 'back':
        u = -(worldX + w);
        v = -(worldY + h);
        break;
      case 'left':
        u = -(worldZ + d);
        v = -(worldY + h);
        break;
      case 'right':
        u = worldZ;
        v = -(worldY + h);
        break;
      default:
        u = 0;
        v = 0;
    }

    return { u, v };
  }
}
